import { useState, useEffect } from 'react';
import { Loader2, Pencil, Trash2, Music2, Image as ImageIcon, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { usePlaylistTracks } from '@/hooks/usePlaylistTracks';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { PLAYLIST_KIND, generateRandomGradient, type Playlist } from '@/lib/musicTypes';

interface EditPlaylistDialogProps {
  playlist: Playlist;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

export function EditPlaylistDialog({ playlist, onSuccess, trigger }: EditPlaylistDialogProps) {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [title, setTitle] = useState(playlist.title);
  const [description, setDescription] = useState(playlist.description || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState(playlist.imageUrl || '');
  const [gradient, setGradient] = useState<string[]>(playlist.gradient || []);
  const [isPublic, setIsPublic] = useState(playlist.isPublic);
  const [isCollaborative, setIsCollaborative] = useState(playlist.isCollaborative);
  const [trackRefs, setTrackRefs] = useState(playlist.trackRefs);
  const [isUploading, setIsUploading] = useState(false);

  const { data: tracks, isLoading: tracksLoading } = usePlaylistTracks(playlist);
  const { mutate: updatePlaylist, isPending } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { toast } = useToast();

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageUrl('');
      setGradient([]);
    }
  };

  const handleRandomizeGradient = () => {
    setGradient(generateRandomGradient());
    setImageFile(null);
    setImageUrl('');
  };

  // Generate preview style
  const previewStyle = imageFile
    ? { backgroundImage: `url(${URL.createObjectURL(imageFile)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : imageUrl
    ? { backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : gradient.length > 0
    ? { background: `linear-gradient(135deg, ${gradient.join(', ')})` }
    : null;

  // Update state when playlist changes
  useEffect(() => {
    setTitle(playlist.title);
    setDescription(playlist.description || '');
    setImageUrl(playlist.imageUrl || '');
    setGradient(playlist.gradient || []);
    setImageFile(null);
    setIsPublic(playlist.isPublic);
    setIsCollaborative(playlist.isCollaborative);
    setTrackRefs(playlist.trackRefs);
  }, [playlist]);

  const handleRemoveTrack = (trackRef: string) => {
    setTrackRefs((refs) => refs.filter((ref) => ref !== trackRef));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a playlist title',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      // Upload image if provided
      let finalImageUrl = imageUrl;
      if (imageFile) {
        console.log('EditPlaylistDialog: Starting image upload');
        toast({ title: 'Uploading cover image...' });
        const [[, imgUrl]] = await uploadFile(imageFile);
        finalImageUrl = imgUrl;
        console.log('EditPlaylistDialog: Image uploaded to:', finalImageUrl);
      }

      const tags: string[][] = [
        ['d', playlist.id],
        ['title', title.trim()],
        ['t', 'playlist'],
        ['alt', `Playlist: ${title.trim()}`],
      ];

      if (description.trim()) {
        tags.push(['description', description.trim()]);
      }

      if (finalImageUrl) {
        tags.push(['image', finalImageUrl]);
      }

      // Add tracks in order
      trackRefs.forEach((ref) => {
        tags.push(['a', ref]);
      });

      // Add category tags
      playlist.tags.forEach((tag) => {
        tags.push(['t', tag]);
      });

      if (isPublic) {
        tags.push(['public', 'true']);
      }

      if (isCollaborative) {
        tags.push(['collaborative', 'true']);
      }

      updatePlaylist(
        {
          kind: PLAYLIST_KIND,
          content: description.trim(),
          tags,
        },
        {
          onSuccess: () => {
            toast({
              title: 'Success',
              description: 'Playlist updated successfully',
            });
            setOpen(false);
            setIsUploading(false);
            onSuccess?.();
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: error.message || 'Failed to update playlist',
              variant: 'destructive',
            });
            setIsUploading(false);
          },
        }
      );
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload image',
        variant: 'destructive',
      });
      setIsUploading(false);
    }
  };

  const handleDelete = () => {
    // Publish a NIP-09 deletion event (kind 5)
    // This requests relays to delete the playlist event
    updatePlaylist(
      {
        kind: 5,
        content: 'Deleted playlist',
        tags: [
          ['e', playlist.event.id],
          ['a', `${PLAYLIST_KIND}:${playlist.pubkey}:${playlist.id}`],
        ],
      },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Playlist deleted successfully',
          });
          setOpen(false);
          setDeleteDialogOpen(false);
          onSuccess?.();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to delete playlist',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm">
              <Pencil className="mr-2 h-4 w-4" />
              Edit Playlist
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Playlist</DialogTitle>
            <DialogDescription>
              Update your playlist details and manage tracks.
            </DialogDescription>
          </DialogHeader>

            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    placeholder="My Awesome Playlist"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isPending || isUploading}
                    maxLength={100}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="A collection of my favorite tracks..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isPending || isUploading}
                    maxLength={500}
                    rows={3}
                  />
                </div>

                {/* Cover Image */}
                <div className="space-y-2">
                  <Label>Cover Image</Label>
                  <div className="flex gap-4">
                    {/* Preview */}
                    <div className="flex-shrink-0">
                      {previewStyle ? (
                        <div
                          className="w-32 h-32 rounded-lg bg-cover bg-center"
                          style={previewStyle}
                        >
                          {!imageFile && !imageUrl && gradient.length > 0 && (
                            <div className="h-full flex items-center justify-center">
                              <Music2 className="h-8 w-8 text-white/80" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-lg border border-dashed border-white/[0.11] bg-white/[0.11] flex items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Controls */}
                    <div className="flex-1 space-y-3">
                      {/* Upload options */}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1 gap-2"
                          onClick={() => document.getElementById('edit-playlist-image')?.click()}
                          disabled={isPending || isUploading}
                        >
                          <ImageIcon className="h-4 w-4" />
                          Upload Image
                        </Button>
                        <Input
                          id="edit-playlist-image"
                          type="file"
                          accept="image/*"
                          onChange={handleImageFileChange}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          onClick={handleRandomizeGradient}
                          disabled={isPending || isUploading}
                        >
                          <Sparkles className="h-4 w-4" />
                          Random
                        </Button>
                      </div>

                      {/* Or URL */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">Or</span>
                        </div>
                      </div>

                      <Input
                        placeholder="Paste image URL"
                        value={imageUrl}
                        onChange={(e) => {
                          setImageUrl(e.target.value);
                          setImageFile(null);
                          setGradient([]);
                        }}
                        disabled={isPending || isUploading}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="public">Public Playlist</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow others to discover this playlist
                    </p>
                  </div>
                  <Switch
                    id="public"
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                    disabled={isPending || isUploading}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="collaborative">Collaborative</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow others to add tracks
                    </p>
                  </div>
                  <Switch
                    id="collaborative"
                    checked={isCollaborative}
                    onCheckedChange={setIsCollaborative}
                    disabled={isPending || isUploading}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Tracks ({trackRefs.length})</Label>
                  {tracksLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <Card key={i}>
                          <CardContent className="p-3">
                            <Skeleton className="h-12 w-full" />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : tracks && tracks.length > 0 ? (
                    <div className="space-y-2">
                      {tracks.map((track, index) => {
                        const trackRef = trackRefs[index];
                        return (
                          <Card key={trackRef}>
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{track.title}</p>
                                  {track.genres.length > 0 && (
                                    <p className="text-sm text-muted-foreground truncate">
                                      {track.genres.join(', ')}
                                    </p>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveTrack(trackRef)}
                                  disabled={isPending || isUploading}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No tracks in this playlist
                    </p>
                  )}
                </div>
              </div>
            </ScrollArea>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isPending || isUploading}
            >
              Delete Playlist
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isPending || isUploading}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={isPending || isUploading}>
                {(isPending || isUploading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{playlist.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
